import { useState, FormEvent, useRef, DragEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/d6tufs76t0doov8ffl1g";

type FormStatus = "idle" | "submitting" | "success" | "error";

const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
const MAX_SIZE_MB = 10;

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSetFile = (f: File) => {
    setFileError(null);
    if (!ALLOWED_TYPES.includes(f.type)) {
      setFileError("Formato no válido. Acepta PDF, JPG o PNG.");
      return;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setFileError(`El archivo supera los ${MAX_SIZE_MB} MB.`);
      return;
    }
    setFile(f);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const removeFile = () => {
    setFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get("mensaje") as string;
    if (message && message.length > 500) return;

    setStatus("submitting");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    if (file) {
      params.append("factura_adjunta", `${file.name} (${formatSize(file.size)})`);
    }

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
        setFile(null);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const contactInfo = [
    { icon: "ri-phone-line", label: "Teléfono", value: "+34 657 891 882" },
    { icon: "ri-mail-line", label: "Email", value: "hola@faroenergia.es" },
    { icon: "ri-time-line", label: "Horario", value: "Lun–Vie, 9:00–18:00" },
    { icon: "ri-map-pin-line", label: "Ubicación", value: "Castellón de la Plana" },
  ];

  return (
    <section id="contacto" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-faro-teal text-sm font-semibold uppercase tracking-widest">
            Contacto
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-faro-navy mt-3 mb-4">
            Solicita tu estudio gratuito
          </h2>
          <p className="text-faro-gray-text text-lg max-w-2xl mx-auto">
            Cuéntanos tu situación y en menos de 24 horas te contactamos para empezar tu análisis energético sin coste ni compromiso.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left: Contact info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-faro-navy rounded-2xl p-8">
              <h3 className="text-white font-semibold text-lg mb-2">¿Hablamos?</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                Estamos aquí para ayudarte. Puedes contactarnos por el formulario, por teléfono o por email. Respondemos siempre con rapidez.
              </p>
              <div className="flex flex-col gap-5">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-faro-teal/20 text-faro-teal-light rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-base`} />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-0.5">{item.label}</p>
                      <p className="text-white text-sm font-medium">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 mt-8 pt-6">
                <p className="text-white/40 text-xs mb-3">Síguenos en redes</p>
                <div className="flex gap-3">
                  {["ri-linkedin-box-fill", "ri-instagram-line", "ri-twitter-x-line"].map((icon) => (
                    <div key={icon} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-faro-teal/20 text-white/50 hover:text-faro-teal-light rounded-lg cursor-pointer transition-all duration-200">
                      <i className={`${icon} text-sm`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Invoice tip card */}
            <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-6 flex gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-amber-500 text-lg" />
              </div>
              <div>
                <p className="text-faro-navy font-semibold text-sm mb-1">¿Tienes tu factura a mano?</p>
                <p className="text-gray-500 text-xs leading-relaxed">
                  Adjuntarla nos permite analizar tu consumo real y ofrecerte un estudio de ahorro mucho más preciso y personalizado.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            {status === "success" ? (
              <div className="bg-faro-teal-pale border border-faro-teal/30 rounded-2xl p-12 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-faro-teal/20 text-faro-teal rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-2xl" />
                </div>
                <h3 className="text-faro-navy font-bold text-xl mb-2">¡Solicitud recibida!</h3>
                <p className="text-faro-gray-text text-sm">
                  Hemos recibido tu solicitud{file ? " y la factura adjunta" : ""}. En menos de 24 horas te enviamos tu estudio de ahorro personalizado, sin coste ni compromiso.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-6 text-faro-teal text-sm font-medium cursor-pointer hover:underline"
                >
                  Enviar otra solicitud
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-faro-gray rounded-2xl p-8 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="nombre" className="text-faro-navy text-sm font-medium">
                      Nombre <span className="text-faro-teal">*</span>
                    </label>
                    <input
                      id="nombre"
                      name="nombre"
                      type="text"
                      required
                      placeholder="Tu nombre completo"
                      className="bg-white border border-faro-gray-mid rounded-md px-4 py-3 text-sm text-faro-navy placeholder-faro-gray-text/60 focus:outline-none focus:border-faro-teal transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="empresa" className="text-faro-navy text-sm font-medium">
                      Empresa <span className="text-faro-gray-text text-xs font-normal">(opcional)</span>
                    </label>
                    <input
                      id="empresa"
                      name="empresa"
                      type="text"
                      placeholder="Nombre de tu empresa"
                      className="bg-white border border-faro-gray-mid rounded-md px-4 py-3 text-sm text-faro-navy placeholder-faro-gray-text/60 focus:outline-none focus:border-faro-teal transition-colors duration-200"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="telefono" className="text-faro-navy text-sm font-medium">
                      Teléfono <span className="text-faro-teal">*</span>
                    </label>
                    <input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      required
                      placeholder="+34 600 000 000"
                      className="bg-white border border-faro-gray-mid rounded-md px-4 py-3 text-sm text-faro-navy placeholder-faro-gray-text/60 focus:outline-none focus:border-faro-teal transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email-field" className="text-faro-navy text-sm font-medium">
                      Correo electrónico <span className="text-faro-teal">*</span>
                    </label>
                    <input
                      id="email-field"
                      name="email"
                      type="email"
                      required
                      placeholder="tu@email.com"
                      className="bg-white border border-faro-gray-mid rounded-md px-4 py-3 text-sm text-faro-navy placeholder-faro-gray-text/60 focus:outline-none focus:border-faro-teal transition-colors duration-200"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="tipo" className="text-faro-navy text-sm font-medium">
                    Tipo de cliente
                  </label>
                  <select
                    id="tipo"
                    name="tipo"
                    className="bg-white border border-faro-gray-mid rounded-md px-4 py-3 text-sm text-faro-navy focus:outline-none focus:border-faro-teal transition-colors duration-200 cursor-pointer"
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="particular">Particular</option>
                    <option value="pyme">Pyme</option>
                    <option value="negocio">Negocio</option>
                    <option value="comunidad">Comunidad de propietarios</option>
                  </select>
                </div>

                {/* ── File upload zone ── */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-faro-navy text-sm font-medium flex items-center gap-1.5">
                    <i className="ri-attachment-2 text-faro-teal" />
                    Adjunta tu factura de luz o gas{" "}
                    <span className="text-faro-gray-text text-xs font-normal">(opcional)</span>
                  </label>

                  {!file ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      className={`relative border-2 border-dashed rounded-xl px-6 py-7 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all duration-200 select-none
                        ${dragOver
                          ? "border-faro-teal bg-faro-teal/5"
                          : "border-faro-gray-mid bg-white hover:border-faro-teal hover:bg-faro-teal/3"
                        }`}
                    >
                      <div className="w-11 h-11 flex items-center justify-center bg-faro-teal/10 rounded-full">
                        <i className="ri-upload-cloud-2-line text-faro-teal text-xl" />
                      </div>
                      <p className="text-faro-navy text-sm font-medium text-center">
                        {dragOver ? "Suelta el archivo aquí" : "Arrastra tu factura o haz clic para seleccionarla"}
                      </p>
                      <p className="text-faro-gray-text text-xs text-center">
                        PDF, JPG o PNG · Máximo {MAX_SIZE_MB} MB
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>
                  ) : (
                    <div className="bg-white border border-faro-teal/40 rounded-xl px-5 py-4 flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-faro-teal/10 rounded-lg shrink-0">
                        <i className={`text-faro-teal text-lg ${file.type === "application/pdf" ? "ri-file-pdf-2-line" : "ri-image-line"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-faro-navy text-sm font-medium truncate">{file.name}</p>
                        <p className="text-faro-gray-text text-xs">{formatSize(file.size)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-faro-teal text-base" />
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-red-50 rounded-full transition-colors cursor-pointer"
                          aria-label="Eliminar archivo"
                        >
                          <i className="ri-close-line text-base" />
                        </button>
                      </div>
                    </div>
                  )}

                  {fileError && (
                    <p className="text-red-400 text-xs flex items-center gap-1">
                      <i className="ri-error-warning-line" /> {fileError}
                    </p>
                  )}

                  <p className="text-faro-gray-text text-xs">
                    Con tu factura podemos ofrecerte un estudio de ahorro mucho más preciso y personalizado.
                  </p>
                </div>

                {/* Mensaje */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="mensaje" className="text-faro-navy text-sm font-medium">
                    Mensaje <span className="text-faro-teal">*</span>
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Cuéntanos brevemente tu situación: tipo de suministro, consumo aproximado, qué problema tienes..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="bg-white border border-faro-gray-mid rounded-md px-4 py-3 text-sm text-faro-navy placeholder-faro-gray-text/60 focus:outline-none focus:border-faro-teal transition-colors duration-200 resize-none"
                  />
                  <p className={`text-xs text-right ${charCount > 480 ? "text-red-400" : "text-faro-gray-text/50"}`}>
                    {charCount}/500
                  </p>
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-sm text-center">
                    Ha ocurrido un error. Por favor, inténtalo de nuevo o contáctanos directamente por teléfono.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="bg-faro-teal hover:bg-faro-teal-light disabled:opacity-60 text-white font-semibold text-base py-4 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap mt-2"
                >
                  {status === "submitting" ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line" />
                      Quiero mi estudio de ahorro gratuito
                    </span>
                  )}
                </button>

                <p className="text-faro-gray-text text-xs text-center">
                  Sin compromiso · Sin coste · Tu privacidad está protegida
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
